import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { DashboardLocIdInfo } from "../../actions/dashboard/dashboardTypes";

import {
  fetchSBActionsNames,
  fetchSBSites,
  getUserPassword,
  userUpdateValues,
} from "../../actions/users/userAsyncActions";
import { ColumnTables } from "./userTablestyping";
import { UserData } from "../../actions/users/userTypes";
import {
  DataSBFiltersValues,
  TableFilters,
} from "../dataTable/dataTableFilters";
import { logOut } from "../layout";

export interface UsersSBFilterValues {
  name: string;
  id: string | number | undefined;
}

export interface CheckboxFilters {
  id: any;
  name: string;
  isSelected: boolean;
}

export interface UsersSBFilters {
  inputSearch: string;
  rolesSelected: CheckboxFilters[];
  locations: {
    valuesSelected: DashboardLocIdInfo[];
    allValuesSelected: boolean;
  };

  actions: {
    valuesSelected: DashboardLocIdInfo[];
    allValuesSelected: boolean;
  };

  varieties: {
    valuesSelected: DashboardLocIdInfo[];
    allValuesSelected: boolean;
  };

  CreationDateSelected: CheckboxFilters[];

  filtersRoles: TableFilters | undefined;
  filtersLocation: TableFilters | undefined;
  filterActions: TableFilters | undefined;
  filterVarieties: TableFilters | undefined;
  filterCreationDate: TableFilters | undefined;
  filtersSelectedAfterFetch: number;
}
export interface userSBFilterValues {
  scopeId: string;
  scenarioId: number;
  name: string;
  id: string | number | undefined;
}

export interface UserTablesState {
  allUserColumnsTable: ColumnTables[];
  rowsNumber: number;
  usersSelected: string[];
  modalUserSelected: boolean;
  showFilters: boolean;
  usersSBFilters: UsersSBFilters;
  searchInputUsersTable: string;
  selectedUser: UserData | null;
  isEditingUser: boolean;

  userValuesUpdate: {
    role: string;
    site: number;
    sites: number[];
    actions: {
      allValuesSelected: boolean;
      valuesSelected: userSBFilterValues[];
    };
    copyOfActions: { valuesSelected: userSBFilterValues[] };
    contactEmail: string;
    phone: string;
    phoneCode: string;
  };
  isUpdateSuccess: boolean;
  isUpdateLoading: boolean;
  isUpdateError: string;

  // filters values
  usersSBFilterValues: DataSBFiltersValues;
  loadingUsersSbFiltersValues: boolean;
  errorFilters: string;

  // user details
  loadingUserDetails: boolean;
  selectedUserRole?: string;

  // get-password
  loadingUserPassword: boolean;
  decipherPassword: string | null;
  showPassword: boolean;
}

export const initialState: UserTablesState = {
  rowsNumber: 25,
  usersSelected: [],
  modalUserSelected: false,
  loadingUserDetails: true,
  allUserColumnsTable: [
    {
      field: "id",
      sortable: true,
      width: 100,
      alwaysHide: true,
    },
    {
      field: "username",
      sortable: false,
      width: 240,
      alwaysHide: false,
    },
    {
      field: "assignedSite",
      sortable: false,
      width: 240,
      alwaysHide: false,
    },
    {
      field: "balance",
      sortable: false,
      width: 240,
      alwaysHide: false,
    },
    {
      field: "assignedAction",
      sortable: false,
      width: 240,
      alwaysHide: false,
    },

    {
      field: "actions",
      sortable: false,
      width: 100,
      alwaysHide: false,
    },
  ],
  showFilters: false,

  usersSBFilters: {
    inputSearch: "",
    rolesSelected: [],
    filtersRoles: undefined,
    filtersLocation: undefined,
    filterActions: undefined,
    filterVarieties: undefined,
    filterCreationDate: undefined,
    filtersSelectedAfterFetch: 0,

    locations: { valuesSelected: [], allValuesSelected: false },

    actions: {
      valuesSelected: [],
      allValuesSelected: false,
    },
    varieties: {
      valuesSelected: [],
      allValuesSelected: false,
    },

    CreationDateSelected: [],
  },

  usersSBFilterValues: {
    locations: [{ name: "", id: undefined }],
    actions: [{ name: "", id: undefined, cerealType: [] }],
  },

  errorFilters: "",
  loadingUsersSbFiltersValues: false,

  searchInputUsersTable: "",
  selectedUser: null,
  isEditingUser: false,
  userValuesUpdate: {
    role: "",
    site: 0,
    sites: [],
    actions: {
      allValuesSelected: false,
      valuesSelected: [],
    },
    copyOfActions: {
      valuesSelected: [],
    },
    contactEmail: "",
    phone: "",
    phoneCode: "",
  },
  isUpdateSuccess: false,
  isUpdateLoading: false,
  isUpdateError: "",

  // get-password
  loadingUserPassword: false,
  decipherPassword: null,
  showPassword: false,
};

const CreateTablesUsersSlice = createSlice({
  name: "CreateTablesUsersReducer",
  initialState,
  reducers: {
    setUserValuesToEdit(state, actions) {
      return {
        ...state,
        userValuesUpdate: actions.payload,
      };
    },
    setEditingUser(state, actions) {
      return {
        ...state,
        isEditingUser: actions.payload,
      };
    },

    setSelectedUser(state, action: PayloadAction<UserData | null>) {
      return {
        ...state,
        selectedUser: action.payload,
      };
    },
    setSearchInput(state, action) {
      return {
        ...state,
        searchInputUsersTable: action.payload,
      };
    },
    setAllUsersTableColumns(state, action) {
      return {
        ...state,
        allUserColumnsTable: action.payload,
      };
    },
    setNumberRowsUsers(state, action) {
      return {
        ...state,
        rowsNumber: action.payload,
      };
    },
    setSelectionModelUsers(state, action) {
      return {
        ...state,
        usersSelected: action.payload,
      };
    },

    showFilters(state, action) {
      return {
        ...state,
        showFilters: action.payload,
      };
    },

    setFiltersConfig(state, action) {
      return {
        ...state,
        usersSBFilters: action.payload,
      };
    },

    setUserUpdateSuccess(state, action) {
      return {
        ...state,
        isUpdateSuccess: action.payload,
      };
    },

    resetTablesState(state) {
      return {
        ...state,
        usersSBFilters: initialState.usersSBFilters,
      };
    },
    logOutUserTables() {
      return initialState;
    },

    resetDecipherPassword(state) {
      return {
        ...state,
        decipherPassword: null,
      };
    },

    setShowPassword(state, action) {
      return {
        ...state,
        showPassword: action.payload,
      };
    },
  },
  extraReducers(builder) {
    builder
      .addCase(userUpdateValues.pending, (state) => {
        state.isUpdateLoading = true;
        state.isUpdateSuccess = false;
      })
      .addCase(userUpdateValues.fulfilled, (state) => {
        state.isUpdateLoading = false;
        state.isUpdateSuccess = true;
        state.selectedUser = null;
        state.isEditingUser = false;
        state.isUpdateError = "";
        state.userValuesUpdate = initialState.userValuesUpdate;
      })
      .addCase(userUpdateValues.rejected, (state, action) => {
        state.isUpdateLoading = false;
        state.isUpdateError = action.payload as string;
      });

    builder
      .addCase(getUserPassword.pending, (state) => {
        state.loadingUserPassword = true;
      })
      .addCase(getUserPassword.fulfilled, (state, action) => {
        state.loadingUserPassword = false;
        state.showPassword = true;
        state.decipherPassword = (action.payload as any)?.password;
      })
      .addCase(getUserPassword.rejected, (state) => {
        state.loadingUserPassword = false;
        state.showPassword = true;
        state.decipherPassword = "Error";
      });

    builder
      .addCase(fetchSBSites.pending, (state) => {
        state.loadingUsersSbFiltersValues = true;
        state.errorFilters = "";
      })
      .addCase(fetchSBSites.fulfilled, (state, action) => {
        state.loadingUsersSbFiltersValues = false;
        state.usersSBFilterValues.locations = action.payload;
      })
      .addCase(fetchSBSites.rejected, (state, action) => {
        state.loadingUsersSbFiltersValues = false;
        state.usersSBFilterValues.locations =
          initialState.usersSBFilterValues.locations;
        state.errorFilters = action.payload as string;
      });
    builder
      .addCase(fetchSBActionsNames.pending, (state) => {
        state.loadingUsersSbFiltersValues = true;
        state.errorFilters = "";
      })
      .addCase(fetchSBActionsNames.fulfilled, (state, action) => {
        state.loadingUsersSbFiltersValues = false;
        state.usersSBFilterValues.actions = action.payload;
      })
      .addCase(fetchSBActionsNames.rejected, (state, action) => {
        state.loadingUsersSbFiltersValues = false;
        state.usersSBFilterValues.actions =
          initialState.usersSBFilterValues.actions;
        state.errorFilters = action.payload as string;
      });

    builder.addCase(logOut, () => {
      return initialState;
    });
  },
});

export const {
  setUserUpdateSuccess,
  setUserValuesToEdit,
  setEditingUser,
  setSelectedUser,
  logOutUserTables,
  setSearchInput,
  setAllUsersTableColumns,
  resetTablesState,
  setNumberRowsUsers,
  setSelectionModelUsers,
  showFilters,
  setFiltersConfig,
  resetDecipherPassword,
  setShowPassword,
} = CreateTablesUsersSlice.actions;

export const CreateTables = CreateTablesUsersSlice.reducer;
